import { faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { exhaustive } from "exhaustive"
import { isPlainObject, sortBy } from "lodash"
import { FC, useEffect, useMemo, useState } from "react"
import { MeResponseConsumerData, MeResponseConsumerType } from "../../Auth/Auth.types"
import { useAuth } from "../../Auth/AuthContext"
import { useClient } from "../../Client/ClientAndUserProvider"
import { API, ServerError } from "../../network/API"
import { Dropdown } from "../../Orders/Components/ExpandableDropdown/ExpandableDropdown"
import { FinalizeButton } from "../../Orders/Components/Form/Buttons/Buttons"
import { ModulePopup } from "../../Orders/Components/ModulePopup/ModulePopup"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { cls } from "../../Shared/cls"
import { notNull } from "../../Shared/notNull"
import {
	ConsumerGroup,
	DynamicGroup,
	GetDynamicConsumerGroupConnection,
	GetDynamicGroupsFixedType,
} from "../CustomerPortalConsumerGroups/CustomerPortalConsumerGroups"
import style from "./DynamicConsumerGroupConsumerAssignmentModal.module.css"

export type DynamicConsumerGroupConsumerAssignmentModalProps = {
	dynamicGroup: DynamicGroup | null
	fixedGroup: EditFixedGroup | null
	dynamicGroupConnection: GetDynamicConsumerGroupConnection | null
	otherConnections: GetDynamicConsumerGroupConnection[]
	allConnections: GetDynamicConsumerGroupConnection[]
	allGroups: DynamicGroup[]
	fixedGroups: Record<GetDynamicGroupsFixedType, ConsumerGroup>
	onClose: () => void
	onSave: () => Promise<void>
}

export type EditFixedGroup = {
	type: "Company" | "PrivatePerson"
	fixedGroup: ConsumerGroup
}

enum SubmitFailType {
	Generic = "Generic",
	GroupNotFound = "GroupNotFound",
}

enum ShowOnlyConsumersMode {
	All = "all",
	ThisGroup = "thisGroup",
	OtherGroups = "otherGroups",
	ChosenNotSaved = "chosenNotSaved",
}

type SetDynamicConsumerGroupConnectionsRequest = {
	dynamicGroupId: string | null
	connections: SetDynamicConsumerGroupConnection[]
}

type SetDynamicConsumerGroupConnection = {
	consumerRef: string
	fromConnectionId: string | null
}

export const DynamicConsumerGroupConsumerAssignmentModal: FC<DynamicConsumerGroupConsumerAssignmentModalProps> = (
	props: DynamicConsumerGroupConsumerAssignmentModalProps,
) => {
	const client = useClient()
	const auth = useAuth()
	const permissions = usePermissions()

	const [originalConsumers] = useState<MeResponseConsumerData[]>(() => {
		let ret = auth.Me?.consumers || []
		if (props.fixedGroup) {
			if (props.fixedGroup.type === "Company") {
				ret = ret.filter((x) => x.type === MeResponseConsumerType.Company)
			} else if (props.fixedGroup.type === "PrivatePerson") {
				ret = ret.filter((x) => x.type === MeResponseConsumerType.Person)
			}
		}

		ret = sortBy(ret, (x) => x.name.normalize("NFC").toLocaleLowerCase())
		return ret
	})

	const [selectedConsumerRefs, setSelectedConsumerRefs] = useState<string[]>([])
	const [consumerRefsToTransfer, setConsumerRefsToTransfer] = useState<SetDynamicConsumerGroupConnection[]>([])

	const [showOnlyConsumersMode, setShowOnlyConsumersMode] = useState<ShowOnlyConsumersMode>(ShowOnlyConsumersMode.All)
	const [filterText, setFilterText] = useState("")

	const [submitting, setSubmitting] = useState(false)
	const [submitFailed, setSubmitFailed] = useState<SubmitFailType | null>(null)

	const shownConsumers = useMemo<MeResponseConsumerData[]>(() => {
		let ret = originalConsumers

		if (showOnlyConsumersMode) {
			exhaustive(showOnlyConsumersMode, {
				[ShowOnlyConsumersMode.All]: () => {},
				[ShowOnlyConsumersMode.ThisGroup]: () => {
					ret = ret.filter((consumer) => {
						if (props.dynamicGroupConnection) {
							return props.dynamicGroupConnection.consumerRefs.includes(consumer.id)
						} else if (props.fixedGroup) {
							let connection: GetDynamicConsumerGroupConnection | null =
								props.allConnections.find((connection) => connection.consumerRefs.includes(consumer.id)) ||
								null

							if (!connection) {
								if (
									consumer.type === MeResponseConsumerType.Person &&
									props.fixedGroup.type === "PrivatePerson"
								) {
									return true
								} else if (
									consumer.type === MeResponseConsumerType.Company &&
									props.fixedGroup.type === "Company"
								) {
									return true
								}
							}
						}

						return false
					})
				},
				[ShowOnlyConsumersMode.OtherGroups]: () => {
					ret = ret.filter((consumer) => {
						if (props.dynamicGroupConnection) {
							return !props.dynamicGroupConnection.consumerRefs.includes(consumer.id)
						} else if (props.fixedGroup) {
							let connection: GetDynamicConsumerGroupConnection | null =
								props.allConnections.find((connection) => connection.consumerRefs.includes(consumer.id)) ||
								null

							if (!connection) {
								if (
									consumer.type === MeResponseConsumerType.Person &&
									props.fixedGroup.type === "PrivatePerson"
								) {
									return false
								} else if (
									consumer.type === MeResponseConsumerType.Company &&
									props.fixedGroup.type === "Company"
								) {
									return false
								}
							}
						}

						return true
					})
				},
				[ShowOnlyConsumersMode.ChosenNotSaved]: () => {
					ret = ret.filter(
						(x) =>
							selectedConsumerRefs.includes(x.id) ||
							consumerRefsToTransfer.find((y) => y.consumerRef === x.id),
					)
				},
			})
		}

		if (filterText) {
			const text = filterText.normalize("NFC").toLocaleLowerCase()
			ret = ret.filter((consumer) => {
				let connection: GetDynamicConsumerGroupConnection | null =
					props.allConnections.find((x) => x.consumerRefs.includes(consumer.id)) || null

				let group: ConsumerGroup | null = null

				if (connection) {
					group = props.allGroups.find((x) => x.id === (connection?.dynamicGroupId || "--"))?.group || null
				} else {
					if (consumer.type === MeResponseConsumerType.Person) {
						group = props.fixedGroups.PrivatePersons
					} else if (consumer.type === MeResponseConsumerType.Company) {
						group = props.fixedGroups.Companies
					}
				}

				return (
					consumer.name.normalize("NFC").toLocaleLowerCase().indexOf(text) > -1 ||
					consumer.identifier.toLocaleLowerCase().indexOf(text) > -1 ||
					(group?.name || "").normalize("NFC").toLocaleLowerCase().indexOf(text) > -1
				)
			})
		}

		return ret
	}, [
		originalConsumers,
		showOnlyConsumersMode,
		filterText,
		props.dynamicGroupConnection,
		props.fixedGroup,
		props.allConnections,
		props.allGroups,
		props.fixedGroups.PrivatePersons,
		props.fixedGroups.Companies,
		selectedConsumerRefs,
		consumerRefsToTransfer,
	])

	useEffect(() => {
		if (props.dynamicGroup === null && props.fixedGroup === null) {
			props.onClose()
		}
	}, [])

	useEffect(() => {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Assign)) {
			props.onClose()
		}
	}, [permissions, props])

	function content() {
		const name = props.dynamicGroup?.group?.name || props.fixedGroup?.fixedGroup.name || ""

		return (
			<div className={style.content}>
				<div className={style.groupName}>{name}</div>

				{selectConsumersSection()}
				<div style={{ marginLeft: "auto" }}>
					{selectedConsumerRefs.length + consumerRefsToTransfer.length}st valda
				</div>
			</div>
		)
	}

	function selectConsumersSection() {
		const consumersConnectedToCurrent = originalConsumers.filter((consumer) => {
			let belongsToCurrentConnection = props.dynamicGroupConnection?.consumerRefs.includes(consumer.id) === true
			let belongsToAnyConnection = props.allConnections.find((connection) =>
				connection.consumerRefs.includes(consumer.id),
			)
			let belongsToCurrentFixedGroup = false
			if (props.fixedGroup && !belongsToAnyConnection) {
				if (consumer.type === MeResponseConsumerType.Person && props.fixedGroup.type === "PrivatePerson") {
					belongsToCurrentFixedGroup = true
				} else if (consumer.type === MeResponseConsumerType.Company && props.fixedGroup.type === "Company") {
					belongsToCurrentFixedGroup = true
				}
			}

			return belongsToCurrentConnection || belongsToCurrentFixedGroup
		})

		let allSelected = exhaustive(showOnlyConsumersMode, {
			[ShowOnlyConsumersMode.All]: () => {
				return (
					selectedConsumerRefs.length + consumerRefsToTransfer.length ===
					originalConsumers.length - consumersConnectedToCurrent.length
				)
			},
			[ShowOnlyConsumersMode.ThisGroup]: () => {
				return false
			},
			[ShowOnlyConsumersMode.OtherGroups]: () => {
				return (
					selectedConsumerRefs.length + consumerRefsToTransfer.length ===
					originalConsumers.length - consumersConnectedToCurrent.length
				)
			},
			[ShowOnlyConsumersMode.ChosenNotSaved]: () => {
				return true
			},
		})

		const hidden =
			showOnlyConsumersMode === ShowOnlyConsumersMode.ThisGroup ||
			showOnlyConsumersMode === ShowOnlyConsumersMode.ChosenNotSaved ||
			consumersConnectedToCurrent.length === originalConsumers.length

		return (
			<>
				{selectConsumersFilterSection()}
				<div className={style.consumersSectionTopRow}>
					<input
						disabled={showOnlyConsumersMode === ShowOnlyConsumersMode.ThisGroup || submitting}
						className={cls({
							[style.hiddenCheckbox]: hidden,
						})}
						type="checkbox"
						checked={allSelected}
						onChange={(x) => {
							if (hidden) {
								return
							}

							if (x.target.checked) {
								const arr1 = originalConsumers.filter((consumer) => {
									const connectionToCurrent =
										props.dynamicGroupConnection?.consumerRefs.includes(consumer.id) === true
									const connectionToOther =
										props.otherConnections.find((connection) =>
											connection.consumerRefs.includes(consumer.id),
										) !== undefined

									if (props.fixedGroup) {
										if (
											consumer.type === MeResponseConsumerType.Person &&
											props.fixedGroup.type === "PrivatePerson"
										) {
											return false
										} else if (
											consumer.type === MeResponseConsumerType.Company &&
											props.fixedGroup.type === "Company"
										) {
											return false
										}
									}

									return !connectionToCurrent && !connectionToOther
								})

								const arr2: SetDynamicConsumerGroupConnection[] = originalConsumers
									.map((consumer) => {
										const otherConnectionLink = props.otherConnections.find((connection) =>
											connection.consumerRefs.includes(consumer.id),
										)

										if (otherConnectionLink) {
											return {
												fromConnectionId: otherConnectionLink.id,
												consumerRef: consumer.id,
											}
										}

										return null
									})
									.filter(notNull)

								setSelectedConsumerRefs(arr1.map((x) => x.id))
								setConsumerRefsToTransfer(arr2)
							} else {
								setSelectedConsumerRefs([])
								setConsumerRefsToTransfer([])
							}
						}}
					/>
					<div>Kund</div>
					<div>Nuvarande kundgrupp</div>
				</div>
				<div className={style.consumersSection}>
					{shownConsumers.map((consumer) => {
						let connection: GetDynamicConsumerGroupConnection | null =
							props.dynamicGroupConnection?.consumerRefs.includes(consumer.id)
								? props.dynamicGroupConnection
								: props.otherConnections.find((x) => x.consumerRefs.includes(consumer.id)) || null

						let group: ConsumerGroup | null = null
						let fixedGroup: EditFixedGroup | null = null
						let dynamicGroup: DynamicGroup | null = null

						if (connection) {
							dynamicGroup =
								props.allGroups.find((x) => x.id === (connection?.dynamicGroupId || "--")) || null
							group = dynamicGroup?.group || null
						} else {
							if (consumer.type === MeResponseConsumerType.Person) {
								group = props.fixedGroups.PrivatePersons
								fixedGroup = {
									type: "PrivatePerson",
									fixedGroup: props.fixedGroups.PrivatePersons,
								}
							} else if (consumer.type === MeResponseConsumerType.Company) {
								group = props.fixedGroups.Companies
								fixedGroup = {
									type: "Company",
									fixedGroup: props.fixedGroups.Companies,
								}
							}
						}

						return (
							<div key={consumer.id} className={style.consumerRow}>
								{selectConsumerRowButtons(consumer, dynamicGroup, fixedGroup)}
								<div className={style.consumerRowName}>
									<div>{consumer.name}</div>
									<div>{consumer.identifier}</div>
								</div>
								<div>{group?.name || ""}</div>
							</div>
						)
					})}
				</div>
			</>
		)
	}

	function selectConsumersFilterSection() {
		return (
			<>
				<div className={style.filterTopRow}>
					<div className={cls(style.searchWrapper, { [style.disabledSearchWrapper]: submitting })}>
						<span className={style.searchIcon}>
							<FontAwesomeIcon icon={faSearch} />
						</span>
						<input
							disabled={submitting}
							type={"search"}
							className={style.searchInput}
							placeholder={"Filtrera kunder"}
							onInput={(e) => {
								if (submitting) {
									return
								}
								setFilterText((e.target as HTMLInputElement).value)
							}}
						/>
					</div>
					<Dropdown
						className={style.filterDropdown}
						disabled={submitting}
						getItems={() => {
							return [
								{
									key: "Alla",
									value: ShowOnlyConsumersMode.All,
								},
								{
									key: "Tillhör denna grupp",
									value: ShowOnlyConsumersMode.ThisGroup,
								},
								{
									key: "Tillhör inte denna grupp",
									value: ShowOnlyConsumersMode.OtherGroups,
								},
								{
									key: "Valda, ej sparade",
									value: ShowOnlyConsumersMode.ChosenNotSaved,
								},
							]
						}}
						onChange={(item) => {
							if (item) {
								setShowOnlyConsumersMode(item.value as ShowOnlyConsumersMode)
							}
						}}
						defaultValue={ShowOnlyConsumersMode.All}
					/>
				</div>
			</>
		)
	}

	function selectConsumerRowButtons(
		consumer: MeResponseConsumerData,
		dynamicGroup: DynamicGroup | null,
		fixedGroup: EditFixedGroup | null,
	) {
		const isSelected = selectedConsumerRefs.includes(consumer.id)
		const otherConnectionLink = props.otherConnections.find((connection) =>
			connection.consumerRefs.includes(consumer.id),
		)
		const otherConnectionLinkGroup = props.allGroups.find((x) => x.id === (otherConnectionLink?.dynamicGroupId || "--"))
		const markedForTransfer = consumerRefsToTransfer.find((x) => x.consumerRef === consumer.id)

		let belongsToSameAsCurrent = false

		if (dynamicGroup && props.dynamicGroup) {
			belongsToSameAsCurrent = dynamicGroup.id === props.dynamicGroup.id
		}

		if (fixedGroup && props.fixedGroup && fixedGroup.type === props.fixedGroup.type) {
			belongsToSameAsCurrent = true
		}

		const hidden = showOnlyConsumersMode === ShowOnlyConsumersMode.ThisGroup || belongsToSameAsCurrent
		return (
			<input
				disabled={showOnlyConsumersMode === ShowOnlyConsumersMode.ThisGroup || submitting || hidden}
				className={cls({
					[style.hiddenCheckbox]: hidden,
				})}
				type="checkbox"
				checked={isSelected || !!markedForTransfer}
				onChange={() => {
					if (hidden) {
						return
					}

					if (isSelected && !otherConnectionLink) {
						setSelectedConsumerRefs((x) => {
							return x.filter((y) => y !== consumer.id)
						})
					} else if (!isSelected && !otherConnectionLink) {
						setSelectedConsumerRefs((x) => {
							return [...x, consumer.id]
						})
					} else if (otherConnectionLink && otherConnectionLinkGroup) {
						if (markedForTransfer) {
							setConsumerRefsToTransfer((x) => [...x.filter((y) => y.consumerRef !== consumer.id)])
						} else {
							setConsumerRefsToTransfer((x) => {
								const newObj = {
									fromConnectionId: otherConnectionLink.id,
									consumerRef: consumer.id,
								}
								return [...x, newObj]
							})
						}
					}
				}}
			/>
		)
	}

	function onSubmit() {
		setSubmitting(true)

		const consumersConnectedToCurrent = originalConsumers
			.map((x) => x.id)
			.filter((consumerRef) => {
				return props.dynamicGroupConnection?.consumerRefs.includes(consumerRef) === true
			})

		const arr1: SetDynamicConsumerGroupConnection[] = selectedConsumerRefs
			.concat(consumersConnectedToCurrent)
			.map((ref) => {
				return {
					consumerRef: ref,
					fromConnectionId: null,
				}
			})

		const arr2: SetDynamicConsumerGroupConnection[] = consumerRefsToTransfer.map((transferRef) => {
			return {
				consumerRef: transferRef.consumerRef,
				fromConnectionId: transferRef.fromConnectionId,
			}
		})

		const req: SetDynamicConsumerGroupConnectionsRequest = {
			dynamicGroupId: props.dynamicGroup?.id || null,
			connections: arr1.concat(arr2),
		}

		API.postWithRetries<void, SetDynamicConsumerGroupConnectionsRequest>(
			`/customer-portal/dynamic-consumer-groups-v1/${client.identifier}/connect`,
			req,
			undefined,
			10,
		)
			.then(async () => {
				await props.onSave()
				setSelectedConsumerRefs([])
				setConsumerRefsToTransfer([])
				setSubmitting(false)
			})
			.catch((err) => {
				setSubmitting(false)
				if (err instanceof ServerError) {
					if (err?.data && isPlainObject(err?.data) && "message" in err?.data) {
						const message = err.data.message

						if (message === "group_not_found") {
							setSubmitFailed(SubmitFailType.GroupNotFound)
						} else {
							setSubmitFailed(SubmitFailType.Generic)
						}
						return
					}
				}
				setSubmitFailed(SubmitFailType.Generic)
			})
	}

	return (
		<ModulePopup
			disableClose={submitting}
			className={style.modal}
			onClose={() => {
				props.onClose()
			}}>
			<div className={style.wrapper}>
				<div className={style.modalHeader}>Koppla kunder till kundgrupp</div>
				{content()}
				<div className={style.buttons}>
					<FinalizeButton
						className={style.submitButton}
						disabled={submitting || (selectedConsumerRefs.length === 0 && consumerRefsToTransfer.length === 0)}
						onClick={() => {
							onSubmit()
						}}>
						Spara
						{submitting ? (
							<FontAwesomeIcon style={{ color: "gray", marginLeft: "15px" }} spin={true} icon={faSpinner} />
						) : null}
					</FinalizeButton>
					<FinalizeButton
						className={style.resetButton}
						disabled={submitting || (selectedConsumerRefs.length === 0 && consumerRefsToTransfer.length === 0)}
						onClick={() => {
							setSelectedConsumerRefs([])
							setConsumerRefsToTransfer([])
						}}>
						Ångra val
					</FinalizeButton>
				</div>
				{submitFailed ? (
					<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
						{exhaustive(submitFailed, {
							[SubmitFailType.Generic]: () => "Något gick fel, vänligen försök igen",
							[SubmitFailType.GroupNotFound]: () =>
								"Den valda gruppen kunde inte hittas, vänligen ladda om sidan och försök igen",
						})}
					</div>
				) : null}
			</div>
		</ModulePopup>
	)
}
